import React, { useMemo, useRef, useState } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const API =
  process.env.NODE_ENV === "production"
    ? "https://ecovanta.onrender.com"
    : "http://localhost:5000";

const DEFAULT_SECTOR_BENCHMARKS = {
  tech: { sectorAverage: 65, topQuartile: 82 },
  energy: { sectorAverage: 58, topQuartile: 78 },
  manufacturing: { sectorAverage: 61, topQuartile: 79 }
};

const benchmarkCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "16px",
  background: "#f8fafc"
};

const benchmarkNumberStyle = {
  fontSize: "24px",
  fontWeight: 700,
  margin: "8px 0",
  color: "#0f766e"
};

const dimensions = [
  {
    name: "Governance",
    questions: [
      "ESG responsibilities are clearly assigned.",
      "Executive leadership actively oversees ESG.",
      "Board-level ESG oversight exists."
    ]
  },
  {
    name: "Strategy",
    questions: [
      "An ESG strategy exists.",
      "ESG objectives are linked to business goals.",
      "Measurable ESG targets are defined."
    ]
  },
  {
    name: "Risk Management",
    questions: [
      "ESG risks are identified.",
      "ESG risks are assessed regularly.",
      "Mitigation plans exist for ESG risks."
    ]
  },
  {
    name: "Environmental Management",
    questions: [
      "Environmental impacts are measured.",
      "Reduction targets are defined.",
      "Environmental initiatives are tracked."
    ]
  },
  {
    name: "Social Impact",
    questions: [
      "Employee wellbeing is monitored.",
      "DEI (Diversity, Equity and Inclusion) policies are implemented.",
      "Stakeholder engagement is structured."
    ]
  },
  {
    name: "ESG Data & Reporting",
    questions: [
      "ESG data collection is standardized.",
      "ESG reports are published.",
      "Data quality controls exist."
    ]
  },
  {
    name: "Technology & Automation",
    questions: [
      "Digital ESG tools are used.",
      "ESG workflows are automated.",
      "Analytics support ESG decisions."
    ]
  }
];

function getMaturityLevel(score) {
  if (score >= 4.5) return "Leading";
  if (score >= 4.0) return "Integrated";
  if (score >= 3.0) return "Managed";
  if (score >= 2.0) return "Developing";
  return "Ad Hoc";
}

function getRecommendation(score) {
  if (score >= 4.5)
    return "Your ESG program demonstrates industry-leading maturity with strong governance, reporting, and continuous improvement.";
  if (score >= 4.0)
    return "ESG is well integrated into operations and decision-making. Focus on optimization and benchmarking.";
  if (score >= 3.0)
    return "You have established ESG processes. The next step is embedding ESG into strategic and operational workflows.";
  if (score >= 2.0)
    return "Your ESG program is developing. Prioritize governance, reporting, and formalized ESG objectives.";
  return "Your ESG program is at an early stage. Focus on governance, ownership, policies, and baseline measurements.";
}

function getPriority(score) {
  if (score < 2) return "Critical";
  if (score < 3) return "High";
  if (score < 4) return "Medium";
  return "Low";
}

function getDimensionRecommendation(name) {
  const recommendations = {
    Governance:
      "Establish formal ESG governance, assign clear ownership, and increase executive and board oversight.",
    Strategy:
      "Integrate ESG objectives into business strategy and define measurable ESG targets.",
    "Risk Management":
      "Implement a formal ESG risk identification, assessment, and mitigation process.",
    "Environmental Management":
      "Measure environmental impacts, establish reduction targets, and track performance.",
    "Social Impact":
      "Strengthen employee wellbeing, DEI (Diversity, Equity and Inclusion) initiatives, and stakeholder engagement programs.",
    "ESG Data & Reporting":
      "Implement standardized ESG data collection and align reporting with recognized frameworks.",
    "Technology & Automation":
      "Adopt ESG software, automate data collection, and use analytics for ESG decision-making."
  };

  return (
    recommendations[name] ||
    "Define clear improvement actions for this ESG dimension."
  );
}

export default function ESGMaturityBenchmark() {
  const [answers, setAnswers] = useState({});
  const [showReport, setShowReport] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  const reportRef = useRef(null);

  const totalQuestions = dimensions.reduce(
    (sum, d) => sum + d.questions.length,
    0
  );

  const dimensionScores = useMemo(() => {
    let questionIndex = 0;

    return dimensions.map((dimension) => {
      let total = 0;

      dimension.questions.forEach(() => {
        total += Number(answers[questionIndex] || 0);
        questionIndex++;
      });

      return {
        name: dimension.name,
        score: Number((total / dimension.questions.length).toFixed(1))
      };
    });
  }, [answers]);

  const radarData = useMemo(() => {
    return dimensionScores.map((dimension) => ({
      dimension: dimension.name,
      score: dimension.score
    }));
  }, [dimensionScores]);

  const recommendationData = useMemo(() => {
    return dimensionScores
      .map((dimension) => ({
        ...dimension,
        priority: getPriority(dimension.score),
        recommendation: getDimensionRecommendation(dimension.name)
      }))
      .sort((a, b) => a.score - b.score);
  }, [dimensionScores]);

  const overallScore = useMemo(() => {
    if (dimensionScores.length === 0) return 0;

    const avg =
      dimensionScores.reduce((sum, d) => sum + d.score, 0) /
      dimensionScores.length;

    return Number(avg.toFixed(1));
  }, [dimensionScores]);

  const benchmarkScore100 = useMemo(() => {
    return Math.round((Number(overallScore || 0) / 5) * 100);
  }, [overallScore]);

  const sectorBenchmark = DEFAULT_SECTOR_BENCHMARKS.tech;

  const benchmarkPosition = useMemo(() => {
    if (benchmarkScore100 >= sectorBenchmark.topQuartile) {
      return "Top quartile";
    }

    if (benchmarkScore100 >= sectorBenchmark.sectorAverage) {
      return "Above sector average";
    }

    return "Below sector average";
  }, [benchmarkScore100, sectorBenchmark]);

  const maturityLevel = getMaturityLevel(overallScore);
  const answeredCount = Object.keys(answers).length;

  const saveAssessment = async () => {
    try {
      setSaveStatus("Saving assessment...");

      const response = await fetch(`${API}/maturity-assessments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          companyName: "",
          overallScore: Number(overallScore),
          maturityLevel,
          dimensionScores,
          recommendations: recommendationData,
          answers,
          benchmarkComparison: {
            score100: benchmarkScore100,
            sectorAverage: sectorBenchmark.sectorAverage,
            topQuartile: sectorBenchmark.topQuartile,
            position: benchmarkPosition,
            sector: "tech"
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Unable to save assessment.");
      }

      setSaveStatus("Assessment saved successfully.");
    } catch (error) {
      console.error("Save assessment failed:", error);
      setSaveStatus(`Save failed: ${error.message}`);
    }
  };

  const setAnswer = (index, value) => {
    setAnswers((prev) => ({
      ...prev,
      [index]: value
    }));
  };

  const downloadPdf = async () => {
    if (!reportRef.current) return;

    try {
      setIsDownloading(true);

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff"
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("ecovanta-esg-maturity-report.pdf");
    } catch (error) {
      console.error("PDF export failed:", error);
      alert("PDF export failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  let globalIndex = 0;

  return (
    <main
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "40px 20px"
      }}
    >
      <h1>ESG Maturity Benchmark</h1>

      <p>
        Assess your organization's ESG maturity across Governance, Strategy,
        Risk, Environment, Social Impact, Reporting, and Technology.
      </p>

      {dimensions.map((dimension) => (
        <div
          key={dimension.name}
          style={{
            marginBottom: "40px",
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "12px"
          }}
        >
          <h2>{dimension.name}</h2>

          {dimension.questions.map((question) => {
            const currentIndex = globalIndex++;

            return (
              <div key={question} style={{ marginBottom: "20px" }}>
                <p>{question}</p>

                <div style={{ display: "flex", gap: "10px" }}>
                  {[1, 2, 3, 4, 5].map((score) => (
                    <button
                      key={score}
                      onClick={() => setAnswer(currentIndex, score)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border:
                          answers[currentIndex] === score
                            ? "2px solid #0f766e"
                            : "1px solid #ddd",
                        background:
                          answers[currentIndex] === score ? "#ccfbf1" : "#fff"
                      }}
                    >
                      {score}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      <div
        style={{
          marginTop: "30px",
          padding: "20px",
          border: "1px solid #ddd",
          borderRadius: "12px"
        }}
      >
        <h3>
          Progress: {answeredCount}/{totalQuestions}
        </h3>

        <button
          disabled={answeredCount !== totalQuestions}
          onClick={() => setShowReport(true)}
          style={{
            padding: "12px 20px",
            borderRadius: "8px",
            cursor: answeredCount === totalQuestions ? "pointer" : "not-allowed",
            opacity: answeredCount === totalQuestions ? 1 : 0.6
          }}
        >
          Generate Report
        </button>
      </div>

      {showReport && (
        <>
          <div
            style={{
              marginTop: "24px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap"
            }}
          >
            <button
              onClick={downloadPdf}
              disabled={isDownloading}
              style={{
                padding: "12px 20px",
                borderRadius: "8px",
                border: "none",
                background: "#0f766e",
                color: "#ffffff",
                cursor: isDownloading ? "not-allowed" : "pointer",
                opacity: isDownloading ? 0.7 : 1
              }}
            >
              {isDownloading ? "Preparing PDF..." : "Download PDF Report"}
            </button>

            <button
              onClick={saveAssessment}
              style={{
                padding: "12px 20px",
                borderRadius: "8px",
                border: "none",
                background: "#0f766e",
                color: "#ffffff",
                cursor: "pointer"
              }}
            >
              Save Assessment
            </button>

              <a
              href="/esg-maturity-history"
              style={{
                //display: "inline-block",
                padding: "9px 18px",
                borderRadius: "8px",
                border: "none",
                background: "#0f766e",
                color: "#ffffff",
                //cursor: "pointer"
                //border: "1px solid #0f766e",
                //color: "#0f766e",
                textDecoration: "none",
                //fontWeight: 600
              }}
            >
              View Assessment History
            </a>

            {saveStatus && (
              <span
                style={{
                  color: "#0f766e",
                  fontWeight: 500
                }}
              >
                {saveStatus}
              </span>
            )}
          </div>

          <section
            ref={reportRef}
            style={{
              marginTop: "20px",
              padding: "30px",
              border: "2px solid #0f766e",
              borderRadius: "16px",
              background: "#ffffff"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
                borderBottom: "1px solid #e5e7eb",
                paddingBottom: "16px"
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: "#0f766e" }}>Ecovanta</h2>
                <p style={{ margin: "4px 0 0", color: "#64748b" }}>
                  ESG Intelligence Platform
                </p>
              </div>

              <div style={{ textAlign: "right", color: "#64748b" }}>
                <strong>ESG Maturity Benchmark</strong>
                <br />
                Generated Report
              </div>
            </div>

            <h2>ESG Maturity Report</h2>

            <h3>Overall Score: {overallScore}/5</h3>

            <h3>Maturity Level: {maturityLevel}</h3>

            <p>{getRecommendation(overallScore)}</p>

            <h3>Benchmark Comparison</h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "16px",
                marginBottom: "30px"
              }}
            >
              <div style={benchmarkCardStyle}>
                <strong>Your Score</strong>
                <div style={benchmarkNumberStyle}>{benchmarkScore100}/100</div>
                <small>{overallScore}/5 maturity score</small>
              </div>

              <div style={benchmarkCardStyle}>
                <strong>Sector Average</strong>
                <div style={benchmarkNumberStyle}>
                  {sectorBenchmark.sectorAverage}/100
                </div>
                <small>Default tech benchmark</small>
              </div>

              <div style={benchmarkCardStyle}>
                <strong>Top Quartile</strong>
                <div style={benchmarkNumberStyle}>
                  {sectorBenchmark.topQuartile}/100
                </div>
                <small>High-performing benchmark</small>
              </div>

              <div style={benchmarkCardStyle}>
                <strong>Position</strong>
                <div style={benchmarkNumberStyle}>{benchmarkPosition}</div>
                <small>Compared with default sector benchmark</small>
              </div>
            </div>

            <h3>ESG Maturity Radar</h3>

            <div style={{ width: "100%", height: "360px" }}>
              <ResponsiveContainer>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="dimension" />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} />
                  <Tooltip />
                  <Radar
                    name="Maturity Score"
                    dataKey="score"
                    stroke="#0f766e"
                    fill="#0f766e"
                    fillOpacity={0.35}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <h3>Improvement Recommendations</h3>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginBottom: "30px"
              }}
            >
              {recommendationData.map((item) => (
                <div
                  key={item.name}
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: "10px",
                    padding: "16px"
                  }}
                >
                  <h4>{item.name}</h4>

                  <p>
                    <strong>Current Score:</strong> {item.score}/5
                  </p>

                  <p>
                    <strong>Priority:</strong> {item.priority}
                  </p>

                  <p>{item.recommendation}</p>
                </div>
              ))}
            </div>

            <h3>Dimension Scores</h3>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse"
              }}
            >
              <thead>
                <tr>
                  <th align="left">Dimension</th>
                  <th align="left">Score</th>
                </tr>
              </thead>

              <tbody>
                {dimensionScores.map((dimension) => (
                  <tr key={dimension.name}>
                    <td>{dimension.name}</td>
                    <td>{dimension.score}/5</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </main>
  );
}